import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Heart, Bell, BellOff, Trash2, Plus, X, TrendingDown, Tag, Loader2, ShoppingCart, RotateCcw, Clock } from 'lucide-react';
import { axiosInstance, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { PricePromotionsSection } from './dashboard/PricePromotionsSection';

const money = (v, cur) => (v === null || v === undefined) ? '' : `${cur ? cur + ' ' : '€'}${v}`;
const FRESH_STYLE = {
  Recent: 'bg-emerald-100 text-emerald-700',
  Older: 'bg-amber-100 text-amber-700',
  Historical: 'bg-gray-200 text-gray-600',
  Unknown: 'bg-gray-100 text-gray-500',
};

const SignIn = ({ label }) => (
  <div className="text-center py-10 text-sm text-gray-500" data-testid="engage-signin">{label}</div>
);

// ============================== FAVOURITES ==============================
export const FavouritesPanel = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [favs, setFavs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [regulars, setRegulars] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, a, r, m] = await Promise.all([
        axiosInstance.get('/price/favourites'),
        axiosInstance.get('/price/alerts'),
        axiosInstance.get('/price/reminders'),
        axiosInstance.get('/price/my-products').catch(() => ({ data: {} })),
      ]);
      setFavs(f.data?.favourites || []);
      setAlerts(a.data?.alerts || []);
      setReminders(r.data?.reminders || []);
      setRegulars(m.data?.products || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) load(); else setLoading(false); }, [user, load]);

  const toggleAlert = async (fav) => {
    try { await axiosInstance.post(`/price/favourites/${fav.id}/alert`, { on: !fav.alertOn }); setFavs((l) => l.map((x) => x.id === fav.id ? { ...x, alertOn: !x.alertOn } : x)); }
    catch { toast.error(t('price.errGeneric')); }
  };
  const removeFav = async (id) => { try { await axiosInstance.delete(`/price/favourites/${id}`); setFavs((l) => l.filter((x) => x.id !== id)); } catch { toast.error(t('price.errGeneric')); } };
  const addToday = async (id) => { try { const r = await axiosInstance.post(`/price/favourites/${id}/add-to-today`); toast.success(t('engage.addedToday', { defaultValue: "Added to Today's Shopping" })); } catch { toast.error(t('price.errGeneric')); } };
  const dismissAlert = async (id) => { try { await axiosInstance.post(`/price/alerts/${id}/dismiss`); setAlerts((l) => l.filter((x) => x.id !== id)); } catch { toast.error(t('price.errGeneric')); } };
  const remindAction = async (rem, action) => {
    try {
      await axiosInstance.post('/price/reminders/action', { productKey: rem.productKey, action, name: rem.name, normalizedName: rem.name, brand: rem.brand, packSize: rem.packSize });
      if (action === 'add') toast.success(t('engage.addedToday', { defaultValue: "Added to Today's Shopping" }));
      setReminders((l) => l.filter((x) => x.productKey !== rem.productKey));
    } catch { toast.error(t('price.errGeneric')); }
  };
  const quickAddRegular = async (p) => {
    try {
      await axiosInstance.post('/price/shopping-list', { name: p.name, qty: 1, brand: p.brand || null, packSize: p.packSize || null });
      toast.success(t('price.addedToListToast'));
    } catch { toast.error(t('price.errGeneric')); }
  };

  if (!user) return <SignIn label={t('engage.favSignIn', 'Sign in to save favourite products.')} />;
  if (loading) return <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-600 mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="price-favourites">
      {/* Price-drop alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2" data-testid="price-alerts">
          <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5"><Bell className="h-4 w-4 text-rose-600" />{t('engage.alertsTitle', 'Price drops')}</p>
          {alerts.map((a) => (
            <div key={a.id} className="rounded-xl border-2 border-rose-100 bg-rose-50/60 p-3 flex items-start gap-2" data-testid={`alert-${a.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">🔔 {a.name}{a.packSize ? ` · ${a.packSize}` : ''}</p>
                <p className="text-[13px] text-gray-700">
                  <span className="line-through text-gray-400">{money(a.wasPrice, a.currency)}</span>{' → '}
                  <span className="font-bold text-rose-700">{money(a.nowPrice, a.currency)}</span>{' '}
                  {t('engage.at', 'at')} {a.shopName}
                </p>
                <p className="text-[12px] text-emerald-700 font-medium">{t('engage.save', 'Save')} {money(a.saving, a.currency)} ({a.pct}%)</p>
                <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full ${FRESH_STYLE[a.freshness?.label] || FRESH_STYLE.Unknown}`}>{a.freshness?.days != null ? t('engage.daysAgo', { n: a.freshness.days, defaultValue: '{{n}} days ago' }) : a.freshness?.label}</span>
              </div>
              <button onClick={() => dismissAlert(a.id)} className="text-gray-400 hover:text-gray-700 p-1" aria-label={t('engage.dismiss', 'Dismiss')} data-testid={`alert-dismiss-${a.id}`}><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}

      {/* Regular-item reminders */}
      {reminders.length > 0 && (
        <div className="space-y-2" data-testid="price-reminders">
          <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5"><Clock className="h-4 w-4 text-indigo-600" />{t('engage.remindersTitle', 'You might need these')}</p>
          {reminders.map((r) => (
            <div key={r.productKey} className="rounded-xl border bg-white p-3" data-testid={`reminder-${r.productKey}`}>
              <p className="text-sm text-gray-900">{t('engage.reminderLine', { name: r.name, days: r.everyDays, defaultValue: 'You usually buy {{name}} about every {{days}} days.' })}</p>
              <div className="flex gap-2 mt-2">
                <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-8 text-[12px]" onClick={() => remindAction(r, 'add')} data-testid={`reminder-add-${r.productKey}`}><Plus className="h-3.5 w-3.5 mr-1" />{t('engage.addToday', "Add to Today's Shopping")}</Button>
                <Button variant="outline" className="rounded-full h-8 text-[12px]" onClick={() => remindAction(r, 'dismiss')} data-testid={`reminder-dismiss-${r.productKey}`}>{t('engage.dismiss', 'Dismiss')}</Button>
                <Button variant="ghost" className="rounded-full h-8 text-[12px] text-gray-500" onClick={() => remindAction(r, 'stop')} data-testid={`reminder-stop-${r.productKey}`}>{t('engage.stop', 'Stop reminding')}</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Favourites list */}
      <div className="space-y-2">
        <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5"><Heart className="h-4 w-4 text-rose-500 fill-rose-500" />{t('engage.favTitle', 'Favourite Products')}</p>
        {favs.length === 0 ? (
          <p className="text-[13px] text-gray-400 py-6 text-center" data-testid="favourites-empty">{t('engage.favEmpty', 'Tap the heart on any product to save it here.')}</p>
        ) : favs.map((f) => (
          <div key={f.id} className="rounded-xl bg-white border p-3 flex items-start gap-2" data-testid={`fav-${f.id}`}>
            <Heart className="h-5 w-5 text-rose-500 fill-rose-500 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate" data-testid={`fav-name-${f.id}`}>{f.name}{f.packSize ? ` · ${f.packSize}` : ''}</p>
              {f.latest ? (
                <p className="text-[12px] text-gray-600">{t('engage.latest', 'Latest')}: <span className="font-semibold text-indigo-700">{money(f.latest.price, f.latest.currency)}</span> {t('engage.at', 'at')} {f.latest.shopName}</p>
              ) : (
                <p className="text-[12px] text-gray-400" data-testid={`fav-noprice-${f.id}`}>{t('engage.noPrice', 'No recent comparison available')}</p>
              )}
              {f.drop && <p className="text-[12px] text-rose-700 font-medium">↓ {money(f.drop.now, f.latest?.currency)} ({f.drop.pct}% {t('engage.cheaper', 'cheaper')})</p>}
              <div className="flex gap-3 mt-1.5 text-[12px]">
                <button onClick={() => addToday(f.id)} className="text-indigo-600 font-medium inline-flex items-center gap-1" data-testid={`fav-addtoday-${f.id}`}><ShoppingCart className="h-3.5 w-3.5" />{t('engage.addToday', "Add to Today's Shopping")}</button>
                <button onClick={() => toggleAlert(f)} className={`inline-flex items-center gap-1 font-medium ${f.alertOn ? 'text-rose-600' : 'text-gray-400'}`} data-testid={`fav-alert-${f.id}`}>
                  {f.alertOn ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}{f.alertOn ? t('engage.alertOn', 'Alerts on') : t('engage.alertOff', 'Alert me')}
                </button>
              </div>
            </div>
            <button onClick={() => removeFav(f.id)} className="text-gray-300 hover:text-rose-600 p-1 shrink-0" aria-label={t('engage.remove', 'Remove')} data-testid={`fav-remove-${f.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>

      {/* Things I buy regularly — one-tap add to Today's Shopping (frequent purchases) */}
      {regulars.length > 0 && (
        <div className="space-y-2" data-testid="buy-often-regulars">
          <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5"><ShoppingCart className="h-4 w-4 text-indigo-600" />{t('price.regularTitle')}</p>
          <div className="space-y-2">
            {regulars.map((p) => (
              <div key={p.id} className="rounded-xl bg-white border p-3 flex items-center gap-2" data-testid={`regular-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{p.name}{p.packSize ? ` · ${p.packSize}` : ''}</p>
                  {p.brand && <p className="text-[11px] text-gray-400 truncate">{p.brand}</p>}
                </div>
                <button onClick={() => quickAddRegular(p)} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-700 transition-colors shrink-0" data-testid={`regular-add-${p.id}`}>
                  <Plus className="h-4 w-4" />{t('price.addToListBtn')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ================================ DEALS ================================
export const DealsPanel = ({ town = '' }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [promoted, setPromoted] = useState([]);
  const [business, setBusiness] = useState(null);

  // Approved-business promotion controls, embedded here instead of sending the
  // owner to the Business Dashboard. Reuses GET /business/me — ordinary users
  // (role !== 'business') never call it and never see the controls.
  useEffect(() => {
    if (!user || user.role !== 'business') { setBusiness(null); return; }
    let alive = true;
    (async () => {
      try {
        const res = await axiosInstance.get('/business/me');
        if (alive) setBusiness(res.data || null);
      } catch { if (alive) setBusiness(null); }
    })();
    return () => { alive = false; };
  }, [user]);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [o, p] = await Promise.all([
          axiosInstance.get('/price/offers'),
          axiosInstance.get('/price/promoted', { params: { town } }),
        ]);
        if (!alive) return;
        setOffers(o.data?.offers || []);
        setPromoted(p.data?.promoted || []);
      } catch { /* ignore */ } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [user, town]);

  const onPromoTap = (p) => { if (p.businessId) window.open(`/business/${p.businessId}`, '_blank'); };

  if (!user) return <SignIn label={t('engage.dealsSignIn', 'Sign in to see local deals.')} />;
  if (loading) return <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-600 mx-auto" /></div>;

  const isApprovedBusiness = user?.role === 'business' && business?.approvalStatus === 'approved';

  return (
    <div className="space-y-4" data-testid="price-deals">
      {/* Approved-business promotion controls — embedded "Promote an Item" + "My Promotions" */}
      {isApprovedBusiness && (
        <div data-testid="deals-business-promotions">
          <PricePromotionsSection business={business} embedded />
        </div>
      )}

      {/* Promoted Near You — clearly separate, never part of the deals ranking */}
      {promoted.length > 0 && (
        <div className="space-y-2" data-testid="promoted-near-you">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="promoted-label">{t('engage.promoted', 'Promoted')}</span>
            <span className="text-[11px] text-gray-400">{t('engage.promotedHint', 'Paid offers — not part of the price comparison')}</span>
          </div>
          {promoted.map((p, i) => (
            <button key={p.id} onClick={() => onPromoTap(p)} className="w-full text-start rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/60 p-3 flex gap-3 hover:bg-amber-50 transition-colors" data-testid={`promoted-card-${i}`}>
              {p.photoUrl && <img src={p.photoUrl} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" />}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">{p.productName}</p>
                <p className="text-[12px] text-gray-600 truncate">{p.shopName}{p.town ? ` · ${p.town}` : ''}</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-base font-bold text-amber-700">{money(p.promoPrice, p.currency)}</span>
                  {p.normalPrice != null && <span className="text-[12px] text-gray-400 line-through">{money(p.normalPrice, p.currency)}</span>}
                </div>
              </div>
            </button>
          ))}
          <div className="border-t border-gray-100 pt-1" />
        </div>
      )}

      {/* Genuine community deals */}
      <div className="space-y-2">
        <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5"><Tag className="h-4 w-4 text-emerald-600" />{t('engage.dealsTitle', 'Offers & Deals')}</p>
        {offers.length === 0 ? (
          <p className="text-[13px] text-gray-400 py-6 text-center" data-testid="deals-empty">{t('engage.dealsEmpty', 'No recent community price drops yet.')}</p>
        ) : offers.map((o, i) => (
          <div key={i} className="rounded-xl bg-white border p-3 flex items-start gap-2" data-testid={`deal-${i}`}>
            <div className="h-9 w-9 rounded-full bg-emerald-50 flex items-center justify-center shrink-0"><TrendingDown className="h-4 w-4 text-emerald-600" /></div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{o.name}{o.packSize ? ` · ${o.packSize}` : ''}</p>
              <p className="text-[12px] text-gray-600">{o.shopName}{o.shopBranch ? ` — ${o.shopBranch}` : ''}</p>
              <p className="text-[13px]"><span className="line-through text-gray-400">{money(o.wasPrice, o.currency)}</span> → <span className="font-bold text-emerald-700">{money(o.nowPrice, o.currency)}</span> <span className="text-emerald-700 font-medium">(-{o.pct}%)</span></p>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${FRESH_STYLE[o.freshness?.label] || FRESH_STYLE.Unknown}`}>{o.freshness?.days != null ? t('engage.daysAgo', { n: o.freshness.days, defaultValue: '{{n}} days ago' }) : o.freshness?.label}</span>
          </div>
        ))}
        <p className="text-[11px] text-gray-400 leading-snug pt-1" data-testid="deals-disclaimer">{t('price.disclaimer')}</p>
      </div>
    </div>
  );
};
